import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { searchAPI } from '../utils/api';

const SearchComponent = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading recent searches:', e);
      }
    }
  }, []);

  // Save search to recent searches
  const saveRecentSearch = (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) return;
    
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 2) {
      if (results.length > 0) setResults([]);
      return;
    }

    // Debounce search
    setIsLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await searchAPI.search(query, 10);
        
        // Add section display names
        const resultsWithNames = (data.results || []).map(p => ({
          ...p,
          sectionName: getSectionDisplayName(p.section)
        }));
        
        setResults(resultsWithNames);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const getSectionDisplayName = (section) => {
    const names = {
      'small-format': 'Small Format',
      'large-format': 'Large Format',
      'swag': 'Promo Swag',
      'awards': 'Awards'
    };
    return names[section] || section;
  };

  // Calculate counts per section
  const sectionCounts = results.reduce((acc, product) => {
    const section = product.section || 'other';
    acc[section] = (acc[section] || 0) + 1;
    return acc;
  }, {});

  // Filter results based on active filter
  const filteredResults = activeFilter === 'all' 
    ? results 
    : results.filter(p => p.section === activeFilter);

  // Available filter sections (only show sections with results)
  const filterSections = [
    { key: 'all', label: 'All' },
    { key: 'awards', label: 'Awards' },
    { key: 'swag', label: 'Promo Swag' },
    { key: 'large-format', label: 'Large Format' },
    { key: 'small-format', label: 'Small Format' },
  ];

  const handleSelect = (product) => {
    saveRecentSearch(query);
    navigate(product.link);
    setQuery('');
    setResults([]);
    setActiveFilter('all');
    onClose();
  };

  const handleRecentClick = (recentQuery) => {
    setQuery(recentQuery);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setQuery('');
      setResults([]);
      setActiveFilter('all');
      onClose();
    } else if (e.key === 'Enter' && query.length >= 2) {
      saveRecentSearch(query);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-ivory-900/60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-ivory-100 rounded-2xl shadow-luxury-lg border border-ivory-300/50 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-4 p-4 border-b border-ivory-300/50">
            <Search className="w-5 h-5 text-gold-600" />
            <Input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search products across all categories..."
              className="flex-1 border-0 bg-transparent text-lg focus-visible:ring-0 font-body placeholder:text-ivory-400"
              data-testid="search-input"
            />
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-gold-600 animate-spin" />
            ) : query && (
              <button 
                onClick={() => setQuery('')}
                className="p-1 hover:bg-ivory-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-ivory-500" />
              </button>
            )}
          </div>

          {/* Filter Chips - only show when there are results */}
          {results.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-3 border-b border-ivory-300/50 overflow-x-auto">
              {filterSections.map(({ key, label }) => {
                const count = key === 'all' ? results.length : (sectionCounts[key] || 0);
                const isActive = activeFilter === key;
                const hasResults = count > 0;
                
                return (
                  <button
                    key={key}
                    onClick={() => hasResults && setActiveFilter(key)}
                    disabled={!hasResults}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all font-body ${
                      isActive
                        ? 'bg-gold-500 text-white shadow-sm'
                        : hasResults
                          ? 'bg-ivory-200 text-ivory-700 hover:bg-ivory-300'
                          : 'bg-ivory-100 text-ivory-400 cursor-not-allowed'
                    }`}
                    data-testid={`search-filter-${key}`}
                  >
                    {label}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                      isActive 
                        ? 'bg-white/20 text-white' 
                        : hasResults 
                          ? 'bg-ivory-300 text-ivory-600' 
                          : 'bg-ivory-200 text-ivory-400'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Results */}
          {filteredResults.length > 0 ? (
            <div className="max-h-[50vh] overflow-y-auto">
              {filteredResults.map((product, index) => (
                <button
                  key={`${product.section}-${product.id}-${index}`}
                  onClick={() => handleSelect(product)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-ivory-200/50 transition-colors text-left border-b border-ivory-300/30 last:border-0"
                  data-testid={`search-result-${product.id}`}
                >
                  <div className="w-16 h-16 rounded-lg bg-ivory-200 overflow-hidden flex-shrink-0">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/100x100/f5f3f0/9a8b7d?text=Product';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-gold-500/10 text-gold-700 rounded-full mb-1 font-body">
                      {product.sectionName}
                    </span>
                    <h4 className="text-sm font-medium text-ivory-800 truncate font-body">
                      {product.name}
                    </h4>
                    <p className="text-xs text-ivory-500 truncate font-body">
                      {product.description}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gold-600 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : query.length >= 2 && !isLoading ? (
            <div className="p-8 text-center">
              <p className="text-ivory-500 font-body">No products found for &quot;{query}&quot;</p>
            </div>
          ) : query.length < 2 && query.length > 0 ? (
            <div className="p-8 text-center">
              <p className="text-ivory-400 text-sm font-body">Type at least 2 characters to search...</p>
            </div>
          ) : (
            <div className="p-6">
              {recentSearches.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium text-ivory-500 uppercase tracking-wider font-body">Recent Searches</p>
                    <button 
                      onClick={clearRecentSearches}
                      className="text-xs text-ivory-400 hover:text-gold-600 transition-colors font-body"
                      data-testid="clear-recent-searches"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map((recent, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleRecentClick(recent)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-ivory-200/50 hover:bg-ivory-200 text-ivory-700 text-sm transition-colors font-body"
                        data-testid={`recent-search-${idx}`}
                      >
                        <Search className="w-3 h-3 text-ivory-400" />
                        {recent}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-ivory-400 text-sm font-body text-center">Start typing to search products...</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchComponent;
